"use client";

import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { X, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Global toast renderer — place once in the root layout alongside <ToastProvider>.
 * Renders all active toasts from the useToast() context as floating notifications.
 */
export function Toaster() {
  const { toasts, dismiss } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed right-0 bottom-0 z-[100] flex flex-col items-end gap-3 p-4 sm:p-6"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => {
        const isDestructive = toast.variant === "destructive";

        return (
          <div
            key={toast.id}
            role="status"
            aria-live="polite"
            className={cn(
              "animate-in slide-in-from-bottom-5 fade-in pointer-events-auto w-full max-w-sm duration-300",
              "rounded-xl border shadow-2xl backdrop-blur-md",
              isDestructive
                ? "border-red-500/30 bg-red-950/80 text-red-100 shadow-red-500/10"
                : "border-white/10 bg-gray-900/80 text-gray-100 shadow-black/20"
            )}
          >
            <div className="flex items-start gap-3 p-4">
              {isDestructive ? (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-400" />
              )}
              <div className="flex-1 space-y-1">
                <p className="text-sm leading-tight font-semibold">
                  {toast.title}
                </p>
                {toast.description && (
                  <p className="text-muted-foreground text-sm">
                    {toast.description}
                  </p>
                )}
              </div>
              <button
                onClick={() => dismiss(toast.id)}
                className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 shrink-0 rounded-lg p-1 transition-colors duration-200"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
